"use client";

import ProductCardSkeleton from "@/components/ui/ProductCardSkeleton";
import ProductCard from "@/components/ui/product-card";
import { COLLECTION_NAMES } from "@/constants/collections";
import { useCart } from "@/contexts/cart-context";
import { EventType, type IAddToCartEvent, publishEvent } from "@/events";
import { useSort } from "@/hooks/useSort";
import { CartStorage } from "@/libs/cart-storage";
import { getRatingFromMetafields } from "@/libs/metafield-utils";
import { convertPaisaToRupees, formatShopifyVariantId } from "@/libs/utils";
import {
  createCart,
  addToCart as shopifyAddToCart,
} from "@/shopify/api/shopify-cart";
import { fetchShopifyCollectionByHandle } from "@/shopify/api/shopify-collection";
import type { IApiCollection } from "@/types/api";
import type { IApiProduct } from "@/types/collection";
import { type SortOptionKey, sortOptions } from "@/types/sorting";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CollectionBanner from "./CollectionBanner";
import FilterComponent from "./FilterComponent";
import SortDropdown from "./SortDropdown";

export default function CollectionsClient({
  collectionname,
}: {
  collectionname: string;
}) {
  const { openCart } = useCart();
  const { t, i18n } = useTranslation();

  const [collections, setCollections] = useState<IApiCollection | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentSort, updateSort } = useSort("featured");

  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1399]);
  const [availability, setAvailability] = useState({
    inStock: true,
    outOfStock: false,
  });
  const [filteredProducts, setFilteredProducts] = useState<IApiProduct[]>([]);

  const [initialInStockCount, setInitialInStockCount] = useState(0);
  const [initialOutOfStockCount, setInitialOutOfStockCount] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const getCollectionName = useCallback(
    (language: string) => {
      return collectionname || COLLECTION_NAMES[language];
    },
    [collectionname],
  );

  const handlePageReload = () => window.location.reload();

  const syncCollectionWithShopifyCart = async (variantId: string) => {
    try {
      const existingCartId = CartStorage.getCartToken();
      let cartId = existingCartId;

      if (!cartId) {
        const newCart = await createCart();
        if (!newCart?.id) return console.error("Failed to create a new cart");
        cartId = newCart.id;
        if (cartId) {
          CartStorage.saveCartToken(cartId);
        }
      }

      const updatedCart = await shopifyAddToCart(
        variantId,
        1,
        cartId || undefined,
      );

      if (updatedCart?.lines?.edges) {
        const shopifyItems = updatedCart.lines.edges.map(
          (edge: any) => edge.node,
        );
        CartStorage.saveShopifyCartItems(shopifyItems);
      }

      openCart();
    } catch (error) {
      console.error("Error syncing with Shopify cart:", error);
    }
  };

  const handleAddToCart = async (product: IApiProduct) => {
    if (!product?.variant?.id) return console.error("Invalid product");

    const event: IAddToCartEvent = {
      type: EventType.ADD_TO_CART,
      productId: product.id,
      productName: product.title,
      price: convertPaisaToRupees(+product.variant.price),
      currency: "INR",
      timestamp: Date.now(),
      variant: product.variant.title || "",
    };

    publishEvent(event);

    const shopifyVariantId = formatShopifyVariantId(product.variant.id);
    await syncCollectionWithShopifyCart(shopifyVariantId);
  };

  // ✅ Safe metafield accessor - memoized to prevent recreation on every render
  const getMetafieldValue = useCallback(
    (
      metafields: any[] | null | undefined,
      key: string,
      namespace: string,
    ): string | undefined => {
      return metafields?.find(
        (meta) => meta?.key === key && meta?.namespace === namespace,
      )?.value;
    },
    [], // No dependencies since this is a pure function
  );

  const fetchCollectionData = useCallback(
    async (
      filterParams?: {
        priceRange?: [number, number];
        availability?: {
          inStock: boolean;
          outOfStock: boolean;
        };
      },
      sortParam?: string,
    ) => {
      try {
        setIsLoading(true);
        setError(null);

        const { sortKey, reverse } =
          sortOptions[sortParam as SortOptionKey] || sortOptions.featured;

        const minPrice = filterParams?.priceRange?.[0] || 0;
        const maxPrice = filterParams?.priceRange?.[1] || 139900;

        let available: boolean | undefined;
        if (filterParams?.availability) {
          const { inStock, outOfStock } = filterParams.availability;
          available =
            inStock && !outOfStock
              ? true
              : !inStock && outOfStock
                ? false
                : undefined;
        }

        const collections = await fetchShopifyCollectionByHandle(
          getCollectionName(i18n.language),
          50,
          sortKey,
          reverse,
          minPrice,
          maxPrice,
          available,
        );

        if (collections?.products?.edges?.length) {
          const transformedProducts = collections.products.edges.map(
            ({ node }: any) => {
              const firstVariant = node.variants.edges[0]?.node;
              const firstImage = node.images.edges[0]?.node;
              const rating = getRatingFromMetafields(node.metafields);

              return {
                id: node.id,
                title:
                  i18n.language === "hi"
                    ? getMetafieldValue(
                        node.metafields,
                        "title_hi",
                        "translations",
                      ) || node.title
                    : node.title,
                handle: node.handle,
                description:
                  i18n.language === "hi"
                    ? getMetafieldValue(
                        node.metafields,
                        "description_hi",
                        "translations",
                      ) || node.description
                    : node.description,
                rating,
                price: Number(node.priceRange.minVariantPrice.amount),
                compareAtPrice: firstVariant?.compareAtPrice?.amount
                  ? Number(firstVariant.compareAtPrice.amount)
                  : undefined,
                image_url: firstImage?.url || "",
                availableForSale:
                  firstVariant?.availableForSale ?? node.availableForSale,
                images: node.images.edges.map(({ node: img }: any) => ({
                  id: img.id || "",
                  url: img.url,
                  alt_text: img.altText,
                })),
                variant: firstVariant
                  ? {
                      id: firstVariant.id,
                      title: firstVariant.title,
                      price: (
                        Number.parseFloat(firstVariant.price.amount) * 100
                      ).toString(),
                      compare_at_price: firstVariant.compareAtPrice?.amount
                        ? (
                            Number.parseFloat(
                              firstVariant.compareAtPrice.amount,
                            ) * 100
                          ).toString()
                        : undefined,
                      sku: firstVariant.sku,
                      length: node.variants.edges.length,
                    }
                  : undefined,
                variants: node.variants.edges.map(({ node: variant }: any) => ({
                  id: variant.id,
                  title: variant.title,
                  price: variant.price.amount,
                  compareAtPrice: variant.compareAtPrice?.amount,
                  sku: variant.sku,
                  available: variant.availableForSale,
                })),
              };
            },
          );

          // Set initial in/out stock count only once
          if (isInitialLoad) {
            const inStock = transformedProducts.filter(
              (p) => p.availableForSale,
            ).length;
            const outOfStock = transformedProducts.length - inStock;
            setInitialInStockCount(inStock);
            setInitialOutOfStockCount(outOfStock);
            setIsInitialLoad(false);
          }

          // Local filter
          let filtered = [...transformedProducts];
          if (filterParams?.priceRange) {
            const [min, max] = filterParams.priceRange;
            filtered = filtered.filter((product) => {
              const price =
                Number(product?.variant?.price) || product.price * 100;
              return price >= min * 100 && price <= max * 100;
            });
          }

          if (filterParams?.availability) {
            const { inStock, outOfStock } = filterParams.availability;
            if (inStock && !outOfStock) {
              filtered = filtered.filter((p) => p.availableForSale);
            } else if (!inStock && outOfStock) {
              filtered = filtered.filter((p) => !p.availableForSale);
            }
          }

          setCollections({
            id: collections.id,
            title: collections.title,
            handle: collections.handle,
            description: collections.description || "",
            metafields: collections.metafields,
            image: collections.image,
            products: transformedProducts,
          });

          setFilteredProducts(filtered);
        } else {
          setCollections(null);
          setFilteredProducts([]);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch products",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [i18n.language, getCollectionName, isInitialLoad, getMetafieldValue],
  );

  const handleFilterChange = (filters: {
    priceRange: [number, number];
    availability: {
      inStock: boolean;
      outOfStock: boolean;
    };
  }) => {
    setPriceRange(filters.priceRange);
    setAvailability(filters.availability);
    fetchCollectionData(filters, currentSort);
  };

  const handleSortChange = (option: SortOptionKey) => {
    updateSort(option);
    fetchCollectionData({ priceRange, availability }, option);
  };

  useEffect(() => {
    fetchCollectionData(undefined, currentSort); // Initial load
  }, [fetchCollectionData, currentSort]);

  if (isLoading) {
    return (
      <div className="container-custom px-4 sm:px-6 max-w-7xl py-8">
        <div className="h-9 md:h-11 bg-gray-200 rounded w-64 mx-auto mb-6 animate-pulse" />
        <div className="flex justify-between items-stretch gap-2 mb-6">
          <div className="h-12 bg-gray-200 rounded-lg w-32 animate-pulse" />
          <div className="h-12 bg-gray-200 rounded-lg w-32 animate-pulse" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {Array.from({ length: 12 }, (_, index) => (
            <ProductCardSkeleton key={`skeleton-${Date.now()}-${index}`} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-custom px-4 sm:px-6 max-w-7xl py-8">
        <div className="text-center text-red-500">{error}</div>
        <button
          id="retry"
          aria-label="retry"
          onClick={handlePageReload}
          className="mt-4 px-4 py-2 bg-black text-white rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <section>
      {collections && <CollectionBanner collection={collections} />}
      <div className="container-custom px-4 sm:px-6 max-w-7xl py-4 md:py-8">
        <h1 className="text-[29.75px] md:text-[35px] text-[#1C1D1D] font-ainslie text-center font-semibold mb-3 md:mb-6">
          {i18n.language === "hi"
            ? getMetafieldValue(
                collections?.metafields,
                "title_hi",
                "translations",
              ) || collections?.title
            : collections?.title}
        </h1>

        <div className="flex justify-between items-stretch gap-2 mb-6">
          <FilterComponent
            isFilterOpen={isFilterOpen}
            setIsFilterOpen={setIsFilterOpen}
            priceRange={priceRange}
            availability={availability}
            inStockCount={initialInStockCount}
            outOfStockCount={initialOutOfStockCount}
            onFilterChange={handleFilterChange}
          />
          <SortDropdown
            sortOption={currentSort}
            onSortChange={handleSortChange}
          />
        </div>

        {filteredProducts.length === 0 ? (
          <div className="text-center font-ainslie text-[24px] md:text-[30px] text-[#1C1D1D] py-8">
            {t("common.noProductsFound")}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                title={product.title}
                price={product?.variant?.price || "0"}
                mrp={product?.variant?.compare_at_price || "0"}
                image={product.image_url || "/placeholder-product.png"}
                handle={product.handle}
                rating={product.rating}
                onAddToCart={() => handleAddToCart(product)}
                inStock={product.availableForSale}
                isLoading={isLoading}
                href={`/collections/${collectionname}/products/${encodeURIComponent(
                  product.handle,
                )}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
